/**
 * Shared "something went wrong" UI — a full-page state for when a screen's
 * data failed to load, and an inline banner for in-flow action failures.
 * Both take the `{ kind, message }` shape produced by lib/apiError.js so the
 * icon/title/tone always match *why* the request failed.
 */
import {
  WifiOff,
  Clock,
  Lock,
  Ban,
  SearchX,
  AlertTriangle,
  CloudOff,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { titleFor } from '../lib/apiError';

const ICONS = {
  network: WifiOff,
  timeout: Clock,
  unauthorized: Lock,
  forbidden: Ban,
  notFound: SearchX,
  validation: AlertTriangle,
  server: CloudOff,
  unknown: AlertCircle,
};

const TONE = {
  network: 'text-amber-500 bg-amber-500/10',
  timeout: 'text-amber-500 bg-amber-500/10',
  unauthorized: 'text-amber-500 bg-amber-500/10',
  validation: 'text-amber-500 bg-amber-500/10',
  notFound: 'text-blue-500 bg-blue-500/10',
  forbidden: 'text-red-500 bg-red-500/10',
  server: 'text-red-500 bg-red-500/10',
  unknown: 'text-red-500 bg-red-500/10',
};

function iconFor(kind) {
  return ICONS[kind] || ICONS.unknown;
}

/** Full-page (or full-section) error state with a retry / secondary action. */
export function ErrorState({
  error,
  onRetry,
  retryLabel = 'Try Again',
  secondaryActionLabel,
  onSecondaryAction,
  className = '',
}) {
  const kind = error?.kind || 'unknown';
  const message = error?.message || 'Something went wrong. Please try again.';
  const Icon = iconFor(kind);

  return (
    <div className={`flex flex-col items-center justify-center text-center py-16 px-6 ${className}`}>
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${TONE[kind] || TONE.unknown}`}>
        <Icon className="w-8 h-8" />
      </div>
      <h3 className="text-lg font-semibold mb-2">{titleFor(kind)}</h3>
      <p className="text-muted-foreground max-w-sm mb-6">{message}</p>
      <div className="flex flex-col gap-2 w-full max-w-xs">
        {onRetry && (
          <Button onClick={onRetry} className="w-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            {retryLabel}
          </Button>
        )}
        {onSecondaryAction && secondaryActionLabel && (
          <Button variant="ghost" onClick={onSecondaryAction} className="w-full">
            {secondaryActionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Inline error banner for in-flow failures (a form submit, a sub-section). */
export function ErrorBanner({ error, onRetry, retryLabel = 'Retry', className = '' }) {
  const kind = error?.kind || 'unknown';
  const message = error?.message || 'Something went wrong. Please try again.';
  const Icon = iconFor(kind);

  return (
    <Alert variant="destructive" className={className}>
      <Icon className="h-4 w-4" />
      <AlertTitle>{titleFor(kind)}</AlertTitle>
      <AlertDescription>
        <p>{message}</p>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} className="mt-2">
            <RefreshCw className="w-3 h-3 mr-1" />
            {retryLabel}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
